// src/components/admin/PublishManuscript.jsx
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  X,
  Check,
  ArrowLeft,
  ArrowRight,
  Save,
  Upload,
  FileText,
  AlertTriangle,
  Globe,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Hash,
  Link2,
  Calendar,
  Users,
  Mail,
  Home,
  ChevronRight,
  Image as ImageIcon,
  Sparkles,
  Layers,
  BookMarked,
  Languages
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import RichTextEditor from '../common/RichTextEditor';
import uploadEditorImage from '../../utils/uploadEditorImage';
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { articleTypes } from '../../types/GlobalVariables';
// Abstract Editor Component with RichTextEditor
const AbstractEditor = React.memo(({ value, onChange, onWordCountChange, error, label, required = false }) => {
  const handleChange = useCallback((content) => {
    onChange(content);
  }, [onChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <RichTextEditor
        value={value || ''}
        onChange={handleChange}
        onWordCountChange={onWordCountChange}
        onImageExtracted={uploadEditorImage}
        placeholder={`Enter your abstract in ${label.toLowerCase()}...`}
        minHeight="250px"
        maxHeight="400px"
        showWordCount={true}
        wordLimit={20000}
        toolbarOptions="full"
      />

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
});

// Manuscript Editor Component with RichTextEditor
const ManuscriptEditor = React.memo(({ value, onChange, onWordCountChange }) => {
  const handleChange = useCallback((content) => {
    onChange(content);
  }, [onChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Manuscript Contents <span className="text-red-500">*</span>
      </label>

      <RichTextEditor
        value={value || ''}
        onChange={handleChange}
        onWordCountChange={onWordCountChange}
        onImageExtracted={uploadEditorImage}
        placeholder="Enter your manuscript contents here..."
        minHeight="350px"
        maxHeight="500px"
        showWordCount={true}
        toolbarOptions="full"
      />
    </div>
  );
});

// Abstract Tabs Component - MOVED OUTSIDE to prevent recreation
const AbstractTabs = React.memo(({ 
  activeTab, 
  onTabChange, 
  formData, 
  formErrors,
  abstractWordCount,
  abstractFrWordCount,
  abstractArWordCount,
  abstractPtgWordCount,
  onAbstractChange,
  onAbstractFrChange,
  onAbstractArChange,
  onAbstractPtgChange,
  onAbstractWordCount,
  onAbstractFrWordCount,
  onAbstractArWordCount,
  onAbstractPtgWordCount
}) => {
  const tabs = [
    { id: 'english', label: 'English', required: true, wordCount: abstractWordCount },
    { id: 'french', label: 'French', required: false, wordCount: abstractFrWordCount },
    { id: 'arabic', label: 'Arabic', required: false, wordCount: abstractArWordCount },
    { id: 'portuguese', label: 'Portuguese', required: false, wordCount: abstractPtgWordCount }
  ];

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200">
        <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`
                px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200
                whitespace-nowrap
                ${activeTab === tab.id
                  ? 'text-[--asfi-purple] border-b-2 border-[--asfi-purple] bg-purple-50/30'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <Languages className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.required && (
                  <span className="text-red-500 text-xs ml-1">*</span>
                )}
                {tab.wordCount > 0 && activeTab !== tab.id && (
                  <span className="text-xs text-gray-400 ml-1">
                    ({tab.wordCount} words)
                  </span>
                )}
              </div>
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-4">
        {activeTab === 'english' && (
          <AbstractEditor
            value={formData.abstract}
            onChange={onAbstractChange}
            onWordCountChange={onAbstractWordCount}
            error={formErrors.abstract}
            label="English Abstract"
            required={true}
          />
        )}
        {activeTab === 'french' && (
          <AbstractEditor
            value={formData.abstract_fr}
            onChange={onAbstractFrChange}
            onWordCountChange={onAbstractFrWordCount}
            error={formErrors.abstract_fr}
            label="French Abstract"
            required={false}
          />
        )}
        {activeTab === 'arabic' && (
          <AbstractEditor
            value={formData.abstract_ar}
            onChange={onAbstractArChange}
            onWordCountChange={onAbstractArWordCount}
            error={formErrors.abstract_ar}
            label="Arabic Abstract"
            required={false}
          />
        )}
        {activeTab === 'portuguese' && (
          <AbstractEditor
            value={formData.abstract_ptg}
            onChange={onAbstractPtgChange}
            onWordCountChange={onAbstractPtgWordCount}
            error={formErrors.abstract_ptg}
            label="Portuguese Abstract"
            required={false}
          />
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mt-4">
        <p className="text-xs text-gray-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-blue-500" />
          English abstract is required. Other languages are optional and will only be shown if provided.
        </p>
      </div>
    </div>
  );
});

const PublishManuscript = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Check if user has required editorial level
  const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);

  if (!isAuthorized) {
    return (
      <Unauthorized
        message="You need editor-in-chief or admin privileges to access this feature."
      />
    );
  }

  // Read query params to auto-select publication type
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryIssue = queryParams.get('issue') === 'true';
  const querySupplement = queryParams.get('supplement') === 'true';
  const hasValidQuery = (queryIssue || querySupplement) && !(queryIssue && querySupplement);
  
  // Selection state for publication type
  const [publicationType, setPublicationType] = useState(
    hasValidQuery ? (queryIssue ? 'issue' : 'supplement') : null
  );

  // Sync is_publication with publicationType when auto-set from query params
  useEffect(() => {
    if (publicationType) {
      setFormData(prev => ({
        ...prev,
        is_publication: publicationType === 'issue'
      }));
    }
  }, [publicationType]);

  // Fetch special issues for dropdown
  useEffect(() => {
    const fetchSpecialIssues = async () => {
      try {
        const response = await axios.get('/publications/special-issues/all');
        if (response.data.status === 'success') {
          setSpecialIssues(response.data.specialIssues);
        }
      } catch (err) {
        console.error('Failed to load special issues', err);
      }
    };
    fetchSpecialIssues();
  }, []);
  
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;

  // Word count states for validation
  const [abstractWordCount, setAbstractWordCount] = useState(0);
  const [abstractFrWordCount, setAbstractFrWordCount] = useState(0);
  const [abstractArWordCount, setAbstractArWordCount] = useState(0);
  const [abstractPtgWordCount, setAbstractPtgWordCount] = useState(0);
  const [contentWordCount, setContentWordCount] = useState(0);

  // Tab state for abstracts
  const [activeAbstractTab, setActiveAbstractTab] = useState('english');

  // Article types


  // Form data state
  const [formData, setFormData] = useState({
    // Article Type
    article_type: '',
    // Title
    title: '',
    // Authors
    authorsArray: '',
    corresponding_author: '',
    // Abstract (Rich Text)
    abstract: '',
    abstract_fr: '',
    abstract_ar: '',
    abstract_ptg: '',
    // Manuscript Contents (Rich Text)
    manuscript_contents: '',
    // Files
    manuscriptCover: null,
    manuscript_file: null,
    Hyperlink: '',
    // Issue Details
    issue_number: '',
    page_number: '',
    doi_number: '',
    // Dates
    date_submitted: '',
    date_reviewed: '',
    date_accepted: '',
    date_published: '',
    // Checkboxes
    editor_choice: false,
    open_access: false,
    is_publication: true, // Default to true (Issue)
    is_old_publication: false,
    is_special_issue: false,
    special_issue_id: '',
    custom_special_issue: ''
  });

  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [manuscriptFilePreview, setManuscriptFilePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [specialIssues, setSpecialIssues] = useState([]);
  const [showCustomSpecialInput, setShowCustomSpecialInput] = useState(false);
  const [customSpecialName, setCustomSpecialName] = useState('');

  // Use refs to track if we're updating to prevent loops
  const isUpdatingRef = useRef(false);

  // Handle publication type selection
  const handlePublicationTypeSelect = (type) => {
    setPublicationType(type);
    setFormData(prev => ({
      ...prev,
      is_publication: type === 'issue' // true for issue, false for supplement
    }));
    // Move to step 1 after selection
    setCurrentStep(1);
    window.scrollTo(0, 0);
  };

  // Handle abstract word count - memoized and stable
  const handleAbstractWordCount = useCallback((content) => {
    if (isUpdatingRef.current) return;
    // Extract plain text from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
    setAbstractWordCount(wordCount);
    
    if (formErrors.abstract) {
      setFormErrors(prev => ({ ...prev, abstract: '' }));
    }
  }, [formErrors.abstract]);

  const handleAbstractFrWordCount = useCallback((content) => {
    if (isUpdatingRef.current) return;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
    setAbstractFrWordCount(wordCount);
    
    if (formErrors.abstract_fr) {
      setFormErrors(prev => ({ ...prev, abstract_fr: '' }));
    }
  }, [formErrors.abstract_fr]);

  const handleAbstractArWordCount = useCallback((content) => {
    if (isUpdatingRef.current) return;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
    setAbstractArWordCount(wordCount);
    
    if (formErrors.abstract_ar) {
      setFormErrors(prev => ({ ...prev, abstract_ar: '' }));
    }
  }, [formErrors.abstract_ar]);

  const handleAbstractPtgWordCount = useCallback((content) => {
    if (isUpdatingRef.current) return;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
    setAbstractPtgWordCount(wordCount);
    
    if (formErrors.abstract_ptg) {
      setFormErrors(prev => ({ ...prev, abstract_ptg: '' }));
    }
  }, [formErrors.abstract_ptg]);

  const handleContentWordCount = useCallback((content) => {
    if (isUpdatingRef.current) return;
    // Extract plain text from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
    setContentWordCount(wordCount);
  }, []);

  // Memoized handlers for form updates
  const handleAbstractChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, abstract: content }));
  }, []);

  const handleAbstractFrChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, abstract_fr: content }));
  }, []);

  const handleAbstractArChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, abstract_ar: content }));
  }, []);

  const handleAbstractPtgChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, abstract_ptg: content }));
  }, []);

  const handleManuscriptChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, manuscript_contents: content }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      [field]: file
    }));

    // Create preview for cover image
    if (field === 'manuscriptCover') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }

    // Show file name for manuscript
    if (field === 'manuscript_file') {
      setManuscriptFilePreview(file.name);
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (formData.is_special_issue) {
        if (!formData.special_issue_id && !formData.custom_special_issue) {
          errors.special_issue = 'Please select or enter a special issue';
        }
      }
    }

    if (step === 2) {
      if (!formData.article_type) {
        errors.article_type = 'Article type is required';
      }
      if (!formData.title) {
        errors.title = 'Title is required';
      }
      if (!formData.authorsArray) {
        errors.authorsArray = 'At least one author is required';
      }
      if (!formData.corresponding_author) {
        errors.corresponding_author = 'Corresponding author email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.corresponding_author)) {
        errors.corresponding_author = 'Email is invalid';
      }
    }

    if (step === 3) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.abstract || '';
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
      
      if (wordCount === 0) {
        errors.abstract = 'English abstract is required';
      } else if (wordCount > 20000) {
        errors.abstract = `English abstract must not exceed 20000 words (current: ${wordCount})`;
      }
      
      const optionalAbstracts = [
        { content: formData.abstract_fr, field: 'abstract_fr', label: 'French', count: abstractFrWordCount },
        { content: formData.abstract_ar, field: 'abstract_ar', label: 'Arabic', count: abstractArWordCount },
        { content: formData.abstract_ptg, field: 'abstract_ptg', label: 'Portuguese', count: abstractPtgWordCount }
      ];
      
      optionalAbstracts.forEach(({ content, field, label, count }) => {
        if (content && content.trim()) {
          const tempDivOptional = document.createElement('div');
          tempDivOptional.innerHTML = content;
          const plainTextOptional = tempDivOptional.textContent || tempDivOptional.innerText || '';
          const wordCountOptional = plainTextOptional.trim() ? plainTextOptional.trim().split(/\s+/).length : 0;
          
          if (wordCountOptional < 2 && wordCountOptional > 0) {
            errors[field] = `${label} abstract is too short (minimum 50 words, current: ${wordCountOptional})`;
          } else if (wordCountOptional > 20000) {
            errors[field] = `${label} abstract must not exceed 20000 words (current: ${wordCountOptional})`;
          }
        }
      });
    }

    if (step === 4) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.manuscript_contents || '';
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
      
      if (wordCount === 0) {
        errors.manuscript_contents = 'Manuscript content is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo(0, 0);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate final step
    if (!validateStep(currentStep)) {
      return;
    }

    setSaveLoading(true);
    setSuccessMessage('');

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add all form fields
      formDataToSend.append('article_type', formData.article_type);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('authorsArray', formData.authorsArray);
      formDataToSend.append('corresponding_author', formData.corresponding_author);
      formDataToSend.append('abstract', formData.abstract);
      formDataToSend.append('abstract_fr', formData.abstract_fr);
      formDataToSend.append('abstract_ar', formData.abstract_ar);
      formDataToSend.append('abstract_ptg', formData.abstract_ptg);
      formDataToSend.append('manuscript_contents', formData.manuscript_contents);
      formDataToSend.append('Hyperlink', formData.Hyperlink || '');
      formDataToSend.append('issue_number', formData.issue_number);
      formDataToSend.append('page_number', formData.page_number);
      formDataToSend.append('doi_number', formData.doi_number);
      formDataToSend.append('date_submitted', formData.date_submitted);
      formDataToSend.append('date_reviewed', formData.date_reviewed);
      formDataToSend.append('date_accepted', formData.date_accepted);
      formDataToSend.append('date_published', formData.date_published);
      formDataToSend.append('editor_choice', formData.editor_choice ? 'true' : 'false');
      formDataToSend.append('open_access', formData.open_access ? 'true' : 'false');
      formDataToSend.append('is_publication', formData.is_publication ? 'true' : 'false');
      formDataToSend.append('is_old_publication', 'false');
      formDataToSend.append('is_special_issue', formData.is_special_issue ? 'true' : 'false');
      formDataToSend.append('special_issue_id', formData.special_issue_id || '');
      formDataToSend.append('custom_special_issue', formData.custom_special_issue || '');

      // Add files if they exist
      if (formData.manuscriptCover) {
        formDataToSend.append('manuscriptCover', formData.manuscriptCover);
      }
      if (formData.manuscript_file) {
        formDataToSend.append('manuscript_file', formData.manuscript_file);
      }

      // Make API call to create publication
      const response = await axios.post('/publications/create-publication', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
         timeout: 300000, // Increase to 5 minutes (300,000 ms) for large files
    maxContentLength: Infinity, // Allow large responses
    maxBodyLength: Infinity // Allow large requests
      });

      if (response.data.status === 'success') {
        setSuccessMessage('Manuscript published successfully!');
        navigate("/editors/admin/manage-publications");
      } else {
        throw new Error(response.data.message || 'Failed to publish manuscript');
      }

    } catch (error) {
      console.error('Error publishing manuscript:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || error.message || 'Failed to publish manuscript'
      }));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/editors/admin/manage-publications');
    }
  };

  // Memoized step content - simplified dependencies
  const stepContent = useMemo(() => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Special Issue</h3>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-800">
                Optionally associate this publication with a special issue. You can select an existing special issue or create a new one.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Is this part of a Special Issue?
              </label>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-sm font-medium ${!formData.is_special_issue ? 'text-purple-700' : 'text-gray-400'}`}>No</span>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, is_special_issue: !prev.is_special_issue, special_issue_id: '', custom_special_issue: '' }));
                    setShowCustomSpecialInput(false);
                    setCustomSpecialName('');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.is_special_issue ? 'bg-purple-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_special_issue ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-sm font-medium ${formData.is_special_issue ? 'text-purple-700' : 'text-gray-400'}`}>Yes</span>
              </div>

              {formErrors.special_issue && (
                <p className="mt-1 text-xs text-red-600">{formErrors.special_issue}</p>
              )}

              {formData.is_special_issue && (
                <div className="mt-4">
                  {!showCustomSpecialInput ? (
                    <select
                      value={formData.special_issue_id}
                      onChange={(e) => {
                        if (e.target.value === '__other__') {
                          setShowCustomSpecialInput(true);
                          setFormData(prev => ({ ...prev, special_issue_id: '' }));
                        } else {
                          setFormData(prev => ({ ...prev, special_issue_id: e.target.value, custom_special_issue: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    >
                      <option value="">Select Special Issue...</option>
                      {specialIssues.map(si => (
                        <option key={si.special_issue_id} value={si.special_issue_id}>
                          {si.special_issue_name}
                        </option>
                      ))}
                      <option value="__other__">Other (type custom)</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customSpecialName}
                        onChange={(e) => {
                          setCustomSpecialName(e.target.value);
                          setFormData(prev => ({ ...prev, custom_special_issue: e.target.value }));
                        }}
                        placeholder="Enter new special issue name..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => { setShowCustomSpecialInput(false); setCustomSpecialName(''); setFormData(prev => ({ ...prev, custom_special_issue: '' })); }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

            {/* Article Type */}
            <div>
              <label htmlFor="article_type" className="block text-sm font-medium text-gray-700 mb-1">
                Article Type <span className="text-red-500">*</span>
              </label>

              <select
                id="article_type"
                value={formData.article_type === 'Other' ? 'Other' :
                  articleTypes.includes(formData.article_type) ? formData.article_type : 'Other'}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    article_type: selectedValue === 'Other' ? '' : selectedValue
                  }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${formErrors.article_type ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="">Select Type</option>
                {articleTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
                <option value="Other">Other (please Specify)</option>
              </select>

              {(formData.article_type === '' ||
                (!articleTypes.includes(formData.article_type) && formData.article_type !== '')) && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={formData.article_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, article_type: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${formErrors.article_type ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Specify Article Type"
                    />
                  </div>
                )}

              {formErrors.article_type && (
                <p className="mt-1 text-xs text-red-600">{formErrors.article_type}</p>
              )}
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${formErrors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter Manuscript Title..."
              />
              {formErrors.title && (
                <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>
              )}
            </div>

            {/* Authors */}
            <div>
              <label htmlFor="authorsArray" className="block text-sm font-medium text-gray-700 mb-1">
                Author(s) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="authorsArray"
                name="authorsArray"
                value={formData.authorsArray}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${formErrors.authorsArray ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter or Paste Author Names Separated With Comma (i.e 'Prof. Ashitey Trebi-Ollennu, Dr. Mulalo Doyoyo')"
              />
              {formErrors.authorsArray && (
                <p className="mt-1 text-xs text-red-600">{formErrors.authorsArray}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Separate author names with commas
              </p>
            </div>

            {/* Corresponding Author Email */}
            <div>
              <label htmlFor="corresponding_author" className="block text-sm font-medium text-gray-700 mb-1">
                Corresponding Author's Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="corresponding_author"
                name="corresponding_author"
                value={formData.corresponding_author}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${formErrors.corresponding_author ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Corresponding Author's email"
              />
              {formErrors.corresponding_author && (
                <p className="mt-1 text-xs text-red-600">{formErrors.corresponding_author}</p>
              )}
            </div>

            {/* Publication Type Indicator */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 flex items-center">
                {formData.is_publication ? (
                  <>
                    <BookMarked className="w-4 h-4 mr-2" />
                    <span>Publishing as: <strong>Issue</strong></span>
                  </>
                ) : (
                  <>
                    <Layers className="w-4 h-4 mr-2" />
                    <span>Publishing as: <strong>Supplement</strong></span>
                  </>
                )}
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Abstract</h3>
            <AbstractTabs
              activeTab={activeAbstractTab}
              onTabChange={setActiveAbstractTab}
              formData={formData}
              formErrors={formErrors}
              abstractWordCount={abstractWordCount}
              abstractFrWordCount={abstractFrWordCount}
              abstractArWordCount={abstractArWordCount}
              abstractPtgWordCount={abstractPtgWordCount}
              onAbstractChange={handleAbstractChange}
              onAbstractFrChange={handleAbstractFrChange}
              onAbstractArChange={handleAbstractArChange}
              onAbstractPtgChange={handleAbstractPtgChange}
              onAbstractWordCount={handleAbstractWordCount}
              onAbstractFrWordCount={handleAbstractFrWordCount}
              onAbstractArWordCount={handleAbstractArWordCount}
              onAbstractPtgWordCount={handleAbstractPtgWordCount}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Manuscript Content</h3>

            <ManuscriptEditor
              value={formData.manuscript_contents}
              onChange={handleManuscriptChange}
              onWordCountChange={handleContentWordCount}
            />

            {formErrors.manuscript_contents && (
              <p className="mt-1 text-xs text-red-600">{formErrors.manuscript_contents}</p>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Publication Details</h3>

            {/* Files */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="manuscriptCover" className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Image (optional)
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col w-full border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center py-6">
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">Click to upload cover image</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
                    </div>
                    <input
                      type="file"
                      id="manuscriptCover"
                      onChange={(e) => handleFileChange(e, 'manuscriptCover')}
                      className="hidden"
                      accept=".jpg, .png, .jpeg"
                    />
                  </label>
                </div>
                {coverImagePreview && (
                  <div className="mt-3">
                    <img src={coverImagePreview} alt="Preview" className="h-20 object-contain rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="manuscript_file" className="block text-sm font-medium text-gray-700 mb-2">
                  Manuscript File (required) *
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col w-full border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center py-6">
                      <FileText className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">Click to upload manuscript file</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, DOC up to 50MB</p>
                    </div>
                    <input
                      type="file"
                      id="manuscript_file"
                      onChange={(e) => handleFileChange(e, 'manuscript_file')}
                      className="hidden"
                      accept=".pdf, .doc, .docx"
                    />
                  </label>
                </div>
                {manuscriptFilePreview && (
                  <p className="mt-2 text-xs text-gray-600">
                    Selected: {manuscriptFilePreview}
                  </p>
                )}
              </div>
            </div>

            {/* Hyperlink */}
            <div>
              <label htmlFor="Hyperlink" className="block text-sm font-medium text-gray-700 mb-1">
                Hyperlink to another published article (optional)
              </label>
              <input
                type="url"
                id="Hyperlink"
                name="Hyperlink"
                value={formData.Hyperlink}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="https://..."
              />
            </div>

            {/* Issue Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="issue_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Number
                </label>
                <input
                  type="text"
                  id="issue_number"
                  name="issue_number"
                  value={formData.issue_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                  placeholder="e.g., 1"
                />
              </div>

              <div>
                <label htmlFor="page_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Page Number
                </label>
                <input
                  type="text"
                  id="page_number"
                  name="page_number"
                  value={formData.page_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                  placeholder="e.g., 1-10"
                />
              </div>

              <div>
                <label htmlFor="doi_number" className="block text-sm font-medium text-gray-700 mb-1">
                  DOI Number
                </label>
                <input
                  type="text"
                  id="doi_number"
                  name="doi_number"
                  value={formData.doi_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                  placeholder="10.xxxx/xxxxx"
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Dates & Options</h3>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="date_submitted" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Submitted <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_submitted"
                  name="date_submitted"
                  required
                  value={formData.date_submitted}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="date_reviewed" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Reviewed <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_reviewed"
                  name="date_reviewed"
                  required
                  value={formData.date_reviewed}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="date_accepted" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Accepted <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_accepted"
                  required
                  name="date_accepted"
                  value={formData.date_accepted}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="date_published" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Published <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_published"
                  name="date_published"
                  required
                  value={formData.date_published}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>
            </div>

            {/* Options Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Editor's Choice Card */}
              <div
                onClick={() => setFormData(prev => ({ ...prev, editor_choice: !prev.editor_choice }))}
                className={`relative overflow-hidden rounded-xl border-2 transition-all cursor-pointer group ${formData.editor_choice
                    ? 'border-[--asfi-purple] bg-[--asfi-purple] bg-opacity-5 shadow-lg'
                    : 'border-gray-200 hover:border-[--asfi-purple] hover:bg-purple-50/30'
                  }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${formData.editor_choice
                        ? 'bg-[--asfi-blue] text-white'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-[--asfi-purple] group-hover:text-white'
                      }`}>
                      <Sparkles className="w-6 h-6" />
                    </div>
                    {formData.editor_choice && (
                      <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                        Selected
                      </div>
                    )}
                  </div>

                  <h4 className={`text-lg ${formData.editor_choice ? 'text-white' : 'text-gray-600'} font-semibold mb-2 flex items-center`}>
                    Editor's Choice
                    <svg className="w-5 h-5 ml-2 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.965 8.521C19.988 8.347 20 8.173 20 8c0-2.379-2.143-4.288-4.521-3.965C14.786 2.802 13.466 2 12 2s-2.786.802-3.479 2.035C6.138 3.712 4 5.621 4 8c0 .173.012.347.035.521C2.802 9.215 2 10.535 2 12s.802 2.785 2.035 3.479A3.976 3.976 0 0 0 4 16c0 2.379 2.138 4.283 4.521 3.965C9.214 21.198 10.534 22 12 22s2.786-.802 3.479-2.035C17.857 20.283 20 18.379 20 16c0-.173-.012-.347-.035-.521C21.198 14.785 22 13.465 22 12s-.802-2.785-2.035-3.479zm-9.01 7.895-3.667-3.714 1.424-1.404 2.257 2.286 4.327-4.294 1.408 1.42-5.749 5.706z" />
                    </svg>
                  </h4>

                  <p className={`text-sm  mb-4  ${formData.editor_choice ? 'text-white' : 'text-gray-600'}   `}>
                    Mark this article as Editor's Choice to highlight it as an exceptional contribution. Featured articles receive special visibility.
                  </p>

                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors mr-3 ${formData.editor_choice
                        ? 'border-[--asfi-blue] bg-[--asfi-blue]'
                        : 'border-gray-300 bg-white'
                      }`}>
                      {formData.editor_choice && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm font-medium ${formData.editor_choice ? 'text-white ' : 'text-white '
                      }`}>
                      {formData.editor_choice ? 'Selected as Editor\'s Choice' : 'Click to select as Editor\'s Choice'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Open Access Card */}
              <div
                onClick={() => setFormData(prev => ({ ...prev, open_access: !prev.open_access }))}
                className={`relative overflow-hidden rounded-xl border-2 transition-all cursor-pointer group ${formData.open_access
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50/30'
                  }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${formData.open_access
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-blue-600 group-hover:text-white'
                      }`}>
                      <Globe className="w-6 h-6" />
                    </div>
                    {formData.open_access && (
                      <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        Selected
                      </div>
                    )}
                  </div>

                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    Open Access
                  </h4>

                  <p className="text-sm text-gray-600 mb-4">
                    Make this article freely available to all readers immediately upon publication. No subscription barriers.
                  </p>

                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors mr-3 ${formData.open_access
                        ? 'border-blue-500 bg-blue-600'
                        : 'border-gray-300 bg-white'
                      }`}>
                      {formData.open_access && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm font-medium ${formData.open_access ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                      {formData.open_access ? 'Open Access enabled' : 'Click to enable Open Access'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Publication Summary</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Article Type: {formData.article_type || 'Not selected'}</li>
                <li>• Title: {formData.title ? (formData.title.length > 50 ? formData.title.substring(0, 50) + '...' : formData.title) : 'Not provided'}</li>
                <li>• Authors: {formData.authorsArray || 'Not provided'}</li>
                <li>• Abstract (English): {abstractWordCount} words</li>
                {abstractFrWordCount > 0 && <li>• Abstract (French): {abstractFrWordCount} words</li>}
                {abstractArWordCount > 0 && <li>• Abstract (Arabic): {abstractArWordCount} words</li>}
                {abstractPtgWordCount > 0 && <li>• Abstract (Portuguese): {abstractPtgWordCount} words</li>}
                <li>• Manuscript: {contentWordCount} words</li>
                <li>• Publication Type: {formData.is_publication ? 'Issue' : 'Supplement'}</li>
                {formData.is_special_issue && <li>• ✓ Special Issue{formData.special_issue_id ? `: ${specialIssues.find(si => si.special_issue_id === formData.special_issue_id)?.special_issue_name || ''}` : formData.custom_special_issue ? `: ${formData.custom_special_issue}` : ''}</li>}
                {formData.editor_choice && <li>• ✓ Editor's Choice</li>}
                {formData.open_access && <li>• ✓ Open Access</li>}
              </ul>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 flex items-start">
                <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Important:</strong> Please review all information before publishing. Once published, the manuscript will be immediately visible to the public. Changes may require additional review.
                </span>
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [
    currentStep,
    formData,
    formErrors,
    abstractWordCount,
    abstractFrWordCount,
    abstractArWordCount,
    abstractPtgWordCount,
    contentWordCount,
    coverImagePreview,
    manuscriptFilePreview,
    activeAbstractTab,
    handleAbstractChange,
    handleAbstractFrChange,
    handleAbstractArChange,
    handleAbstractPtgChange,
    handleManuscriptChange,
    handleAbstractWordCount,
    handleAbstractFrWordCount,
    handleAbstractArWordCount,
    handleAbstractPtgWordCount,
    handleContentWordCount,
    handleInputChange,
    handleFileChange,
    specialIssues,
    showCustomSpecialInput,
    customSpecialName
  ]);

  // If no publication type selected, show selection cards
  if (!publicationType) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header with Breadcrumb */}
        <div className="relative py-16 bg-gradient-to-r from-[--asfi-purple] to-[--asfi-blue] overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 border border-white/20">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Publish New Content
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                Choose what type of content you want to publish
              </p>
            </div>
          </div>

          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg className="w-full h-12 text-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                opacity=".25" fill="currentColor"></path>
              <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                opacity=".5" fill="currentColor"></path>
              <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                fill="currentColor"></path>
            </svg>
          </div>
        </div>

        {/* Selection Cards */}
        <div className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Issue Card */}
              <div
                onClick={() => handlePublicationTypeSelect('issue')}
                className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-white p-8 cursor-pointer hover:border-[--asfi-purple] hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[--asfi-blue] opacity-5 rounded-bl-full group-hover:opacity-10 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-[--asfi-blue] bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[--asfi-blue] group-hover:text-white transition-all duration-300">
                    <BookMarked className="w-10 h-10 text-white group-hover:text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Issue</h2>
                  <p className="text-gray-600 mb-6">
                    Publish content as part of a regular journal issue. Includes volume, issue number, and page ranges.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Regular journal issue</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Volume and issue numbering</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Page numbers</span>
                    </div>
                  </div>
                  <button className="mt-8 w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg group-hover:bg-[--asfi-blue] group-hover:text-white transition-colors font-medium">
                    Create Issue Publication
                  </button>
                </div>
              </div>

              {/* Supplement Card */}
              <div
                onClick={() => handlePublicationTypeSelect('supplement')}
                className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-white p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-5 rounded-bl-full group-hover:opacity-10 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-blue-500 bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-blue-500 group-hover:text-white transition-all duration-300">
                    <Layers className="w-10 h-10 text-blue-500 group-hover:text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Supplement</h2>
                  <p className="text-gray-600 mb-6">
                    Publish supplementary content such as conference proceedings, special collections, or additional materials.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Conference proceedings</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Special collections</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span>Supplementary materials</span>
                    </div>
                  </div>
                  <button className="mt-8 w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg group-hover:bg-blue-500 group-hover:text-white transition-colors font-medium">
                    Create Supplement
                  </button>
                </div>
              </div>
            </div>

            {/* Cancel Button */}
            <div className="mt-8 text-center">
              <button
                onClick={handleCancel}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form return
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header with Breadcrumb */}
      <div className="relative py-16 bg-gradient-to-r from-[--asfi-purple] to-[--asfi-blue] overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 border border-white/20">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Publish New {formData.is_publication ? 'Issue' : 'Supplement'}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Fill in the details below to publish a new {formData.is_publication ? 'issue article' : 'supplement'}
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-12 text-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill="currentColor"></path>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 md:py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{successMessage}</span>
              <span className="ml-2 text-sm">Redirecting to supplements page...</span>
            </div>
          )}

          {/* Error Message */}
          {formErrors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{formErrors.submit}</span>
            </div>
          )}

          {/* Main Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step < currentStep
                          ? 'bg-green-600 text-white'
                          : step === currentStep
                            ? 'bg-[--asfi-purple] text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                    >
                      {step < currentStep ? <Check className="w-4 h-4" /> : step}
                    </div>
                    {step < totalSteps && (
                      <div className={`w-16 h-1 mx-2 ${step < currentStep ? 'bg-green-600' : 'bg-gray-200'
                        }`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Special Issue</span>
                <span>Basic Info</span>
                <span>Abstract</span>
                <span>Content</span>
                <span>Details</span>
                <span>Options</span>
              </div>
            </div>

            {/* Step Title */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Step {currentStep} of {totalSteps}: {
                  currentStep === 1 ? 'Special Issue' :
                    currentStep === 2 ? 'Basic Information' :
                      currentStep === 3 ? 'Abstract' :
                        currentStep === 4 ? 'Manuscript Content' :
                          currentStep === 5 ? 'Publication Details' :
                            'Dates & Options'
                }
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step Content */}
              <div className="min-h-[400px]">
                {stepContent}
              </div>

              {/* Navigation Buttons */}
              <div className="mt-8 flex justify-between border-t pt-6">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  disabled={currentStep === 1}
                  className={`px-6 py-2.5 border border-gray-300 rounded-lg flex items-center gap-2 font-medium ${currentStep === 1
                      ? 'opacity-50 cursor-not-allowed bg-gray-100'
                      : 'hover:bg-gray-50 transition-colors'
                    }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to go back? Your publication type selection will be lost.')) {
                        setPublicationType(null);
                        setCurrentStep(1);
                      }
                    }}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Change Type
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="px-6 py-2.5 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 font-medium"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={saveLoading || successMessage}
                      className="px-6 py-2.5 bg-[--asfi-blue] text-white rounded-lg hover:bg-[--asfi-purple] transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Publishing...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Publish {formData.is_publication ? 'Issue' : 'Supplement'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishManuscript;